/* eslint-disable max-lines */
import React, { useMemo } from 'react';
// import { PulsarMenuBar } from '@pulsarml/avalon-ui';
import {
  AlarmClock,
  ArrowRightFromLine,
  BarChart4,
  Bell,
  BotMessageSquare,
  Box,
  CircleAlert,
  CircleUserRound,
  ClipboardList,
  Database,
  FileChartColumn,
  LayoutDashboard,
  Settings
} from 'lucide-react';
import { I18n, Translate } from '@utils/i18n';
import {
  useFeatureFlags,
  useFeatureFlagsAnyEnabled,
  useShowAiBuilder,
  useShowLegacyProduction,
  useShowProduction
} from '@hooks/useFeatureFlags';

import PulsarMenuBar, { MenuItem } from '../../../components/PulsarMenuBar';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '@clean-code/hooks/usePermissions';
import { useUserProfile } from '_clean-code/api/hooks/useUserProfile';
import { CountBadge } from '@pulsarml/avalon-ui';
import { useAlertsDrawer } from '_clean-code/contexts/AlertsDrawerContext';

function AlertsUnreadBadge() {
  const { unreadCount } = useAlertsDrawer();
  if (unreadCount === 0) return null;
  return (
    <CountBadge className="tw-text-white tw-font-normal" count={unreadCount} />
  );
}

/**
 * Marks a menu entry as an in-development feature. Rendered in the option's
 * badge slot, so it only shows while the sidebar is expanded.
 */
function BetaBadge() {
  return (
    <span className="tw-rounded tw-bg-brand-surface-subtle tw-px-1.5 tw-py-0.5 tw-font-sans tw-text-[10px] tw-font-medium tw-uppercase tw-leading-none tw-tracking-[0.5px] tw-text-body">
      <Translate value="beta" />
    </span>
  );
}

function AppMenu() {
  const location = useLocation();

  const { canOpen, usesRolePermissions } = usePermissions();
  const { userData } = useUserProfile();
  const userEmail = userData?.email;

  const isProductionEnabled = useShowProduction();
  const isLegacyProductionEnabled = useShowLegacyProduction();
  const showAiBuilder = useShowAiBuilder();
  const { reportSubscription, realtimeV2, operatorsV2, alertsV2 } =
    useFeatureFlags();
  const { availability } = useFeatureFlagsAnyEnabled();

  const MENU_OPTIONS: Record<string, MenuItem> = useMemo(
    () => ({
      realTime: {
        id: 'real-time',
        label: <Translate value="machines_sidebar" />,
        to: '/realtime',
        active: true,
        icon: <AlarmClock strokeWidth={1.5} color="#374151" />
      },
      home: {
        id: 'home',
        isNew: false,
        label: <Translate value="dashboard" />,
        to: '/dashboard',
        icon: <LayoutDashboard strokeWidth={1.5} color="#374151" />
      },

      aiAnalytics: {
        id: 'ai-analytics',
        isNew: false,
        label: <Translate value="ai_analytics" />,
        to: '/ai-analytics',
        icon: <BotMessageSquare strokeWidth={1.5} color="#374151" />,
        badge: <BetaBadge />
      },

      databases: {
        id: 'databases',
        label: <Translate value="databases" />,
        to: '/databases',
        active: true,
        icon: <Database strokeWidth={1.5} color="#374151" />
      },
      production: {
        id: 'production',
        label: <Translate value="production_sidebar" />,
        to: '/production',
        active: true,
        icon: <Box strokeWidth={1.5} color="#374151" />
      },
      stops: {
        id: 'stops',
        label: <Translate value="stops" />,
        to: '/stops',
        icon: <CircleAlert strokeWidth={1.5} color="#374151" />
      },
      historical: {
        id: 'analytics',
        isNew: false,
        label: <Translate value="analytics_sidebar" />,
        to: '/analytics',
        icon: <BarChart4 strokeWidth={1.5} color="#374151" />
      },
      ...(availability
        ? {
            metrics: {
              id: 'metrics',
              label: <Translate value="metrics_table" />,
              to: '/metrics',
              icon: <FileChartColumn strokeWidth={1.5} color="#374151" />
            },
            ...(operatorsV2
              ? {
                  operators: {
                    id: 'operators',
                    label: <Translate value="operators" />,
                    to: '/operators',
                    icon: <CircleUserRound strokeWidth={1.5} color="#374151" />
                  }
                }
              : {})
          }
        : {}),
      ...(alertsV2
        ? {
            historicalAlerts: {
              id: 'historical-alerts',
              label: <Translate value="historical_alerts" />,
              to: '/view-alerts',
              icon: <Bell strokeWidth={1.5} color="#374151" />,
              badge: <AlertsUnreadBadge />
            }
          }
        : {}),
      reports: {
        id: 'reports',
        label: <Translate value="reports" />,
        to: '/reports',
        icon: <ClipboardList strokeWidth={1.5} color="#374151" />
      },
      export: {
        id: 'export',
        label: <Translate value="data_reports" />,
        to: '/data/reports',
        icon: <ArrowRightFromLine strokeWidth={1.5} color="#374151" />
      },
      settings: {
        id: 'settings',
        label: <Translate value="settings_sidebar" />,
        options: [] as any,
        icon: <Settings strokeWidth={1.5} color="#374151" />
      },
      ...(availability
        ? {
            settings_stopLabels: {
              id: 'stop_labels',
              label: <Translate value="stops" />,
              to: '/stopsettings',
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_alerts: {
              id: 'alerts_settings_sidebar',
              label: <Translate value="alerts_settings_sidebar" />,
              to: '/alerts/set-alerts',
              isNew: false,
              icon: <></>
            }
          }
        : {}),
      ...(availability
        ? {
            settings_idleShifts: {
              id: 'idleShifts',
              label: <Translate value="idleShifts" />,
              to: '/shift-not-considered',
              icon: <></>
            }
          }
        : {}),
      settings_schedule: {
        id: 'shift_schedule',
        label: <Translate value="shift_schedule" />,
        to: '/shift-settings',
        isNew: false,
        icon: <></>
      },
      settings_machine: {
        id: 'machine_settings',
        label: <Translate value="machine_settings" />,
        isNew: false,
        to: '/machine-settings',
        icon: <></>
      },
      settings_goals: {
        id: 'goals_settings',
        label: <Translate value="goals" />,
        isNew: false,
        to: '/settings/goals',
        icon: <></>
      },
      settings_user: {
        id: 'users_settings',
        label: <Translate value="users_settings" />,
        isNew: false,
        to: '/users-settings',
        icon: <></>
      },
      settings_integrations: {
        id: 'integrations',
        label: <Translate value="integrations" />,
        isNew: true,
        to: '/integrations',
        icon: <></>
      },
      settings_production: {
        id: 'production-settings',
        isNew: true,
        label: <Translate value="production" />,
        to: isProductionEnabled
          ? '/production-settings/sku'
          : '/production-settings/nominal-speed',
        icon: <></>
      },
      settings_realtime: {
        id: 'realtime_settings',
        label: <Translate value="machines_sidebar" />,
        to: '/realtime-settings/standard-view',
        icon: <></>
      },
      tv_settings: {
        id: 'tv_settings',
        label: <Translate value="ui_config" />,
        isNew: true,
        to: '/ui-config/tv',
        icon: <></>
      },
      operatorPlanning: {
        id: 'operator-planning',
        label: <Translate value="operators" />,
        to: '/operator-settings',
        icon: <></>
      }
    }),
    [alertsV2, availability, isProductionEnabled, operatorsV2]
  );

  const menuData = useMemo(() => {
    const menuOptions: MenuItem[] = [
      MENU_OPTIONS.home,
      MENU_OPTIONS.realTime,
      MENU_OPTIONS.databases,
      ...(showAiBuilder ? [MENU_OPTIONS.aiAnalytics] : []),
      // ...(availability ? [MENU_OPTIONS.stops] : []),
      MENU_OPTIONS.historical,
      ...(availability ? [MENU_OPTIONS.metrics] : []),
      // ...(availability && operatorsV2 ? [MENU_OPTIONS.operators] : []),
      ...(alertsV2 ? [MENU_OPTIONS.historicalAlerts] : []),
      ...(reportSubscription ? [MENU_OPTIONS.reports] : []),
      MENU_OPTIONS.export
    ];
    if (isLegacyProductionEnabled) {
      menuOptions.splice(3, 0, MENU_OPTIONS.production);
    }
    const permissionSettingsOptions = (): MenuItem[] => [
      ...(availability && canOpen('stopSettings')
        ? [MENU_OPTIONS.settings_stopLabels]
        : []),
      ...(availability && operatorsV2 && canOpen('operatorSettings')
        ? [MENU_OPTIONS.operatorPlanning]
        : []),
      ...(availability && canOpen('alertSettings')
        ? [MENU_OPTIONS.settings_alerts]
        : []),
      ...(canOpen('goals') ? [MENU_OPTIONS.settings_goals] : []),
      ...(isProductionEnabled && canOpen('productionSettings')
        ? [MENU_OPTIONS.settings_production]
        : []),
      ...(availability && canOpen('shiftsNotConsidered')
        ? [MENU_OPTIONS.settings_idleShifts]
        : []),
      ...(canOpen('shiftSettings') ? [MENU_OPTIONS.settings_schedule] : []),
      realtimeV2 ? MENU_OPTIONS.settings_realtime : MENU_OPTIONS.tv_settings,
      ...(canOpen('machineSettings') ? [MENU_OPTIONS.settings_machine] : []),
      ...(canOpen('usersSettings') ? [MENU_OPTIONS.settings_user] : []),
      ...(canOpen('integrations') ? [MENU_OPTIONS.settings_integrations] : [])
    ];

    const loginRoleSettingsOptions = (): MenuItem[] => {
      const role = userData?.role;
      const isAdmin = role !== 'user';
      const isSuperAdmin = role === 'superadmin' || role === 'superuser';
      if (!userEmail || !isAdmin) {
        return [
          realtimeV2 ? MENU_OPTIONS.settings_realtime : MENU_OPTIONS.tv_settings
        ];
      }
      const options = [
        ...(availability
          ? [
              MENU_OPTIONS.settings_stopLabels,
              ...(operatorsV2 ? [MENU_OPTIONS.operatorPlanning] : []),
              MENU_OPTIONS.settings_alerts
            ]
          : []),
        MENU_OPTIONS.settings_goals,
        ...(availability ? [MENU_OPTIONS.settings_idleShifts] : []),
        MENU_OPTIONS.settings_schedule,
        realtimeV2 ? MENU_OPTIONS.settings_realtime : MENU_OPTIONS.tv_settings,
        MENU_OPTIONS.settings_machine
      ];
      if (isProductionEnabled) {
        options.splice(4, 0, MENU_OPTIONS.settings_production);
      }
      if (isSuperAdmin) {
        options.push(
          MENU_OPTIONS.settings_user,
          MENU_OPTIONS.settings_integrations
        );
      }
      return options;
    };

    // Without role permissions the menu is the one the login role got before permissions,
    // including its order.
    const settingsOptions: MenuItem[] = usesRolePermissions
      ? permissionSettingsOptions()
      : loginRoleSettingsOptions();
    const settingsOption = {
      ...MENU_OPTIONS.settings,
      options: settingsOptions
    };

    menuOptions.push(settingsOption);

    return {
      mainOptions: menuOptions
    };
  }, [
    MENU_OPTIONS,
    availability,
    reportSubscription,
    isProductionEnabled,
    realtimeV2,
    canOpen,
    usesRolePermissions,
    userData,
    userEmail,
    operatorsV2,
    alertsV2,
    isLegacyProductionEnabled,
    showAiBuilder
  ]);

  const selectedOption = Object.values(MENU_OPTIONS).find(
    (item) => item.to === location.pathname
  );

  return (
    <PulsarMenuBar
      options={menuData.mainOptions}
      selectedOptionId={selectedOption?.id}
      isNewText={I18n.t('new')}
    />
  );
}

export default AppMenu;
